/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/* eslint-disable no-unused-vars */
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { SearchResults } from '@ccsearch/search';
import { Redirect } from 'react-router-dom';
import { setForceHideNavbar, setLeftNavOpen } from '../../actions/appActions';
import getConfig from '../../../config';

import { SEARCH_ICONS, clientID, APPS, SEARCH_TABS_CONSTANTS } from './SearchConstants';
import { searchQuery, setSearchReferrer } from '../../actions/searchActions';
import AnalyticsUtil from '../../../utils/AnalyticsUtil';
import SearchUtils from './SearchUtils';
import SearchHostContext from './SearchHostContext';
import './SearchResultsPage.scss';

const UrlParse = require('url-parse');

/* eslint-enable no-unused-vars */

type Props = {
	locale: string;
	env: string;
	subscriptionInfo: SubscriptionInfoType;
	productEntitlements: Object;
	history: { goBack: Function; push: Function };
	searchQuery: Function;
	setForceHideNavbar: Function;
	setSearchReferrer: Function;
	forceHideNavbar: boolean;
	setLeftNavOpen: Function;
	searchReferrer: string;
	searchQueryValue: string;
};

type ResultObjectType = {
	appName?: string;
	openUrl?: string;
};

class SearchResultsPage extends Component<Props> {
	constructor(props: Props) {
		super(props);

		window.cchome.util.disableHTMLScroll();
		if (document.body && document.body.style && document.body.style.overscrollBehavior) {
			// $FlowFixMe
			document.body.style.overscrollBehavior = 'none'; // We put this here so we can prevent the weird bouncing scroll thing that happens across Search Page -- we don't want that effect
		}
		// First, get the Host Context.
		const searchHostContext = new SearchHostContext();
		searchHostContext.setHistory(this.props.history);

		// HOSTCONTEXT: TODO: Do I want to do this inside the constructor?
		this.getDefaultTab();
		searchHostContext.setSubscriptionEntitlements(this.props.subscriptionInfo, this.props.productEntitlements);
	}

	componentDidMount() {
		const featureFlags = window.cchome.util.getAllFeatures();
		this.props.setLeftNavOpen(false);
		if (
			window.cchome.util.testBucket20190430CCSearchRollout() === 'testA' ||
			window.cchome.util.testBucket20190430CCSearchRollout() === 'testB'
		) {
			// Enable UI Intervention/Knowledge card for new SRP
			window.cchome.util.testBucket20190606UIinterventionTest();

			// Enable USS Contextual Parameters for new SRP
			if (featureFlags.indexOf('ccsearch_enable_uss_contextual_parameters') === -1) {
				featureFlags.push('ccsearch_enable_uss_contextual_parameters');
				window.cchome.util.testBucket20190815USSContextualParametersTest();
			}
			// Enable USS Auto-Complete Contextual Parameters
			if (featureFlags.indexOf('ccsearch_enable_uss_auto_complete_contextual_parameters') === -1) {
				featureFlags.push('ccsearch_enable_uss_auto_complete_contextual_parameters');
			}
		}
	}

	componentWillUnmount() {
		this.props.searchQuery('');

		switch (window.cchome.util.currentBreakpoint) {
			case 'L':
			case 'LM':
			case 'M':
			case 'MS':
				this.props.setLeftNavOpen(true);
				break;
		}

		window.cchome.util.enableHTMLScroll();
		if (document.body && document.body.style && document.body.style.overscrollBehavior) {
			// $FlowFixMe
			document.body.style.overscrollBehavior = 'auto'; // Put the effect back when we go back to CCHome
		}
	}

	/**
	 * Fetches the original 'location' of where the user executed a search before landing in the search page of CCHome Web.
	 */
	getPreviousLocation() {
		let prevLocationValue = 'home'; // If there's no referrer, we just default assume we're from home.
		const eventReferrer = sessionStorage.getItem('event_referrer');
		if (eventReferrer) {
			const prevLocation = JSON.parse(eventReferrer).referrer;
			// TODO: Need some kind of mapping of CCHome web views to basic strings.
			if (prevLocation.includes('/home')) {
				prevLocationValue = 'home'; // This is redundant but this is to make it obvious we need to talk about what string constants we return into the search component for hostLocations
			} else if (prevLocation.includes('/learn')) {
				prevLocationValue = 'learn';
			} else if (prevLocation.includes('/apps')) {
				prevLocationValue = 'apps';
			} else {
				prevLocationValue = 'yourWork';
			}
		}
		return prevLocationValue;
	}

	/**
	 * Specifies the default tab for the Search Results Page to land in (if needed).
	 */
	getDefaultTab() {
		let defaultTab = ''; // TODO: Later, when config is merged, make this '' empty string
		if (document.referrer && this.props.searchReferrer.length === 0) {
			// We do this === 0 check because document.referrer stays as a value even as you go through other pages of CCHome, and the value only gets cleared when refreshing/new tab/new session etc.
			const assetsHostname = getConfig('ASSET_URL_HOSTNAME');
			const xdHostname = getConfig('XD_URL_HOSTNAME');

			const prefRefUrlParse = new UrlParse(document.referrer, true);
			const prefRefUrlParseQueries = prefRefUrlParse.query;
			const isFromSearch = prefRefUrlParseQueries.fromsearch; // if it exists (ie. we set it before), then it should be true. if it doesn't exist (ie. recents one up), then it's false.

			window.cchome.util.logger.log('Search Get Default Tab', 'prefRefUrlParse', prefRefUrlParse);
			window.cchome.util.logger.log('Search Get Default Tab', 'prefRefUrlParseQueries', prefRefUrlParseQueries);
			window.cchome.util.logger.log('Search Get Default Tab', 'isFromSearch', isFromSearch);

			if (
				(prefRefUrlParse.hostname === assetsHostname || prefRefUrlParse.hostname === xdHostname) &&
				isFromSearch
			) {
				window.cchome.util.logger.log('Search Get Default Tab', 'setting Your Work Tab');
				defaultTab = SEARCH_TABS_CONSTANTS.YOUR_WORK_TAB;
				this.props.setSearchReferrer(document.referrer);
			}
		}

		// First, get the Host Context.
		const searchHostContext = new SearchHostContext();
		// Set the tab inside the SearchHostContext
		searchHostContext.setSearchDefaultTab(defaultTab);

		return defaultTab;
	}

	/**
	 * [getCurrentQuery gets the current query from url]
	 * @return {[array]}
	 */
	getCurrentQuery() {
		const hasQuery = location.search.indexOf('?query') > -1;
		let currentQuery = '';

		if (hasQuery) {
			currentQuery = decodeURIComponent(location.search.split('&')[0].replace('?query=', ''));
		}

		return currentQuery;
	}

	/**
	 * Method that is called when a 'Apps' result is clicked on.
	 * @param {ResultObjectType} resultObject
	 */
	onClickAppsResult(resultObject: ResultObjectType) {
		// TODO: Specify in flow that this is an APPS Result Object
		if (resultObject.appName) {
			for (const appObj of APPS) {
				// Go through array of APPS
				if (appObj.APP_NAME === resultObject.appName) {
					if (resultObject.appName === 'app.search.creative_cloud.label') {
						window.open(appObj.APP_ROUTE, '_blank');
					} else {
						this.props.history.push(appObj.APP_ROUTE);
					}
				}
			}
		} else {
			// error or something here
			window.cchome.util.logger.error('Search Results Page', 'Failed Apps Result App Tag', resultObject);
		}
	}

	/**
	 * Method that is clicked when opening a result object with a openUrl in a new tab.
	 * @param {ResultObjectType} resultObject
	 */
	onClickOpenUrlResult(resultObject: ResultObjectType) {
		if (resultObject && resultObject.openUrl) {
			window.open(resultObject.openUrl, '_blank');
		}
	}

	/**
	 * Method that is clicked when opening a result object with a openUrl in the same window.
	 * @param {ResultObjectType} resultObject
	 */
	onClickOpenUrlResultNoTab(resultObject: ResultObjectType) {
		if (resultObject && resultObject.openUrl) {
			/**
			 * CCH-3590 - XD uses referrer to resolve back button behavior. IE does not update the
			 * referrer data if JS opens a new window. For this to work correctly, we are adding this
			 * as a workaround.
			 *
			 * window.open(resultObject.openUrl, '_self');
			 */
			const link = document.createElement('a');
			const body = document.body;

			const linkHref = resultObject.openUrl;
			const linkHrefUrl = new UrlParse(linkHref, true);
			// Get the queries of the openUrl.
			const linkHrefUrlQueries = linkHrefUrl.query;

			// Get the queries and set the 'fromsearch' query.
			linkHrefUrlQueries.fromsearch = true;
			linkHrefUrl.set('query', linkHrefUrlQueries);

			window.cchome.util.logger.log('Search Results Page', 'Click No Tab Url', linkHrefUrl);

			if (link && body) {
				link.href = linkHrefUrl.href;
				link.target = '_self';
				body.appendChild(link);
				link.click();
			}
		}
	}

	/**
	 * Method to pass into the SearchResultsPage view that controls the functionality of the onClicks of each individual result.
	 * @param {string} type
	 * @param {ResultObjectType} resultObject
	 */
	onClickCard(type: string, resultObject: ResultObjectType) {
		switch (type) {
			case 'stock':
			case 'learn':
			case 'adobeForum':
			case 'lightroom':
			case 'spark':
			case 'sparkTemplates':
				this.onClickOpenUrlResult(resultObject);
				break;
			case 'cloud_docs':
			case 'creative_cloud':
			case 'libraries':
			case 'mobile_creations':
			case 'xd_artifacts':
				this.onClickOpenUrlResultNoTab(resultObject);
				break;
			case 'apps':
				this.onClickAppsResult(resultObject);
				break;
			default:
				window.cchome.util.logger.warn(
					'Search Results Page',
					'Unrecognized result type in onClickCard handler',
					type,
				);
		}
	}

	/**
	 * Method to pass into the SearchResultsPage that controls the functionality of the "Show All" link for each row of results.
	 * @param {string} type
	 * @param {ResultObjectType} resultObject
	 */
	onClickShowAll(type: string, resultObject: ResultObjectType) {
		switch (type) {
			case 'stock':
			case 'learn':
			case 'adobeForum':
			case 'lightroom':
			case 'spark':
			case 'sparkTemplates':
				this.onClickOpenUrlResult(resultObject);
				break;
			case 'cloud_docs':
			case 'creative_cloud':
			case 'libraries':
			case 'mobile_creations':
			case 'xd_artifacts':
				this.onClickOpenUrlResultNoTab(resultObject);
				break;
			default:
				window.cchome.util.logger.warn(
					'Search Results Page',
					'Unrecognized result type in onClickShowAll handler',
					type,
				);
		}
	}

	render() {
		// First, get the Host Context.
		const searchHostContext = new SearchHostContext();

		// Add the feature flag to enable the new SRP, if the user falls into the test bucket
		// and if the flag is not already enabled.
		// Enable Search Auto-Complete for users that fall into the "testA" bucket or "testB" bucket for the new SRP test.
		const featureFlags = window.cchome.util.getAllFeatures();
		if (
			window.cchome.util.testBucket20190430CCSearchRollout() === 'testA' ||
			window.cchome.util.testBucket20190430CCSearchRollout() === 'testB'
		) {
			if (featureFlags.indexOf('temp_enable_new_srp') === -1) {
				featureFlags.push('temp_enable_new_srp');
			}
			if (featureFlags.indexOf('ccsearch_enable_uss_contextual_parameters') === -1) {
				featureFlags.push('ccsearch_enable_uss_contextual_parameters');
				window.cchome.util.testBucket20190815USSContextualParametersTest();
			}
			if (featureFlags.indexOf('temp_enable_search_auto_suggestion_server_uss') === -1) {
				featureFlags.push('temp_enable_search_auto_suggestion_server_uss');
			}
			// Enable USS Auto-Complete Contextual Parameters
			if (featureFlags.indexOf('ccsearch_enable_uss_auto_complete_contextual_parameters') === -1) {
				featureFlags.push('ccsearch_enable_uss_auto_complete_contextual_parameters');
			}
			// Enable UI Intervention/Knowledge card for new SRP
			window.cchome.util.testBucket20190606UIinterventionTest();
		}

		// Enable UI Intervention/Knowledge card for new SRP
		if (window.cchome.util.hasFeature('temp_enable_ui_intervention')) {
			featureFlags.push('temp_enable_ui_intervention');
		}

		searchHostContext.setFeatureFlags(featureFlags);

		return (
			<div className="search-results-container-wrapper">
				<SearchResults searchQuery={this.props.searchQueryValue} searchHostContext={searchHostContext} />
			</div>
		);
	}
}

function mapStateToProps(state) {
	return {
		forceHideNavbar: state.app.forceHideNavbar,
		subscriptionInfo: state.profile.subscriptionInfo,
		productEntitlements: state.profile.productEntitlements,
		searchReferrer: state.search.searchReferrer,
		searchQueryValue: state.search.searchQuery,
	};
}

export default {
	component: connect(mapStateToProps, { searchQuery, setForceHideNavbar, setLeftNavOpen, setSearchReferrer })(
		SearchResultsPage,
	),
};
